import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { splitSheetsAPI } from '../services/api';
import HelpGuide from '../components/HelpGuide';

interface Collaborator {
  // Basic Information
  full_legal_name: string;
  stage_name?: string;
  email: string;
  phone?: string;
  
  // Role and Contribution
  role: string;
  contribution_type: string;
  contribution_description?: string;
  ownership_percentage: number;
  
  // SA Legal Information
  id_number?: string;
  passport_number?: string;
  nationality?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
  date_of_birth?: string;
  
  // Publishing and PRO Information
  publisher_name?: string;
  publisher_contact?: string;
  samro_membership?: string;
  samro_account_number?: string;
  capasso_membership?: string;
  capasso_account_number?: string;
  sampra_membership?: string;
  sampra_account_number?: string;
  impra_membership?: string;
  impra_account_number?: string;
  
  // Royalty Breakdown
  writer_share?: number;
  producer_share?: number;
  performer_share?: number;
  
  // Minor Protection
  is_minor?: boolean;
  guardian_name?: string;
  guardian_id_number?: string;
  guardian_relationship?: string;
  guardian_contact?: string;
}

interface SplitSheetData {
  // Basic Song Information
  song_title: string;
  alternate_titles?: string;
  song_details?: string;
  date_of_creation: string;
  session_date?: string;
  
  // International Codes
  isrc_code?: string;
  iswc_code?: string;
  
  // Project Information
  album_name?: string;
  project_name?: string;
  genre?: string;
  
  // Recording Metadata
  studio_name?: string;
  studio_location?: string;
  recording_engineer?: string;
  mixer?: string;
  instruments_used?: string;
  samples_used?: string;
  sample_clearances?: string;
  
  // Work Type and Rights
  work_type: string;
  copyright_owner?: string;
  
  // Publishing and Administration
  publisher_name?: string;
  admin_name?: string;
  publisher_contact?: string;
  
  // PRO Information
  samro_membership?: string;
  capasso_membership?: string;
  sampra_membership?: string;
  impra_membership?: string;
  
  // Legal and Jurisdiction
  governing_law: string;
  jurisdiction: string;
  agreement_date: string;
  effective_date: string;
  expiry_date?: string;
  
  collaborators: Collaborator[];
}

const CreateSplitSheetSA: React.FC = () => {
  const [formData, setFormData] = useState<SplitSheetData>({
    song_title: '',
    alternate_titles: '',
    song_details: '',
    date_of_creation: new Date().toISOString().split('T')[0],
    session_date: '',
    isrc_code: '',
    iswc_code: '',
    album_name: '',
    project_name: '',
    genre: '',
    studio_name: '',
    studio_location: '',
    recording_engineer: '',
    mixer: '',
    instruments_used: '',
    samples_used: '',
    sample_clearances: '',
    work_type: 'musical_work',
    copyright_owner: '',
    publisher_name: '',
    admin_name: '',
    publisher_contact: '',
    samro_membership: '',
    capasso_membership: '',
    sampra_membership: '',
    impra_membership: '',
    governing_law: 'South African Law',
    jurisdiction: 'South Africa',
    agreement_date: new Date().toISOString().split('T')[0],
    effective_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    collaborators: [
      { 
        full_legal_name: '', 
        stage_name: '',
        email: '', 
        role: 'Artist', 
        contribution_type: 'Composer',
        ownership_percentage: 0,
        nationality: 'South African',
        country: 'South Africa'
      }
    ]
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  const navigate = useNavigate();

  const addCollaborator = () => {
    setFormData({
      ...formData,
      collaborators: [...formData.collaborators, { 
        full_legal_name: '', 
        stage_name: '',
        email: '', 
        role: 'Artist', 
        contribution_type: 'Composer',
        ownership_percentage: 0,
        nationality: 'South African',
        country: 'South Africa'
      }]
    });
  };

  const removeCollaborator = (index: number) => {
    if (formData.collaborators.length > 1) {
      const newCollaborators = formData.collaborators.filter((_, i) => i !== index);
      setFormData({ ...formData, collaborators: newCollaborators });
    }
  };

  const updateCollaborator = (index: number, field: keyof Collaborator, value: string | number | boolean) => {
    const newCollaborators = [...formData.collaborators];
    newCollaborators[index] = { ...newCollaborators[index], [field]: value };
    setFormData({ ...formData, collaborators: newCollaborators });
  };

  const updateFormData = (field: keyof SplitSheetData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const calculateTotalPercentage = () => {
    return formData.collaborators.reduce((sum, collab) => sum + (parseFloat(String(collab.ownership_percentage)) || 0), 0);
  };

  const validateForm = () => {
    // Basic validation
    if (!formData.song_title.trim()) {
      setError('Song title is required');
      return false;
    }

    if (!formData.date_of_creation) {
      setError('Date of creation is required');
      return false;
    }

    if (formData.collaborators.some(c => !c.full_legal_name.trim() || !c.email.trim())) {
      setError('All collaborators must have a full legal name and email');
      return false;
    }

    // SA Legal validation
    if (formData.collaborators.some(c => !c.id_number && !c.passport_number)) {
      setError('All collaborators must provide either SA ID number or passport number');
      return false;
    }

    const total = calculateTotalPercentage();
    if (Math.abs(total - 100) > 0.01) {
      setError(`Total ownership percentage must equal 100%. Current total: ${total.toFixed(2)}%`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await splitSheetsAPI.createSouthAfrican({
        songTitle: formData.song_title,
        songDetails: formData.song_details,
        workType: formData.work_type,
        copyrightOwner: formData.copyright_owner,
        publishingRights: formData.publisher_name,
        recordLabel: formData.admin_name,
        isrcCode: formData.isrc_code,
        iswcCode: formData.iswc_code,
        releaseDate: formData.date_of_creation,
        recordingDate: formData.session_date,
        recordingLocation: formData.studio_location,
        governingLaw: formData.governing_law,
        jurisdiction: formData.jurisdiction,
        agreementDate: formData.agreement_date,
        effectiveDate: formData.effective_date,
        expiryDate: formData.expiry_date,
        // Additional SA fields
        alternateTitles: formData.alternate_titles,
        dateOfCreation: formData.date_of_creation,
        sessionDate: formData.session_date,
        albumName: formData.album_name,
        projectName: formData.project_name,
        genre: formData.genre,
        studioName: formData.studio_name,
        studioLocation: formData.studio_location,
        recordingEngineer: formData.recording_engineer,
        mixer: formData.mixer,
        instrumentsUsed: formData.instruments_used,
        samplesUsed: formData.samples_used,
        sampleClearances: formData.sample_clearances,
        publisherName: formData.publisher_name,
        adminName: formData.admin_name,
        publisherContact: formData.publisher_contact,
        samroMembership: formData.samro_membership,
        capassoMembership: formData.capasso_membership,
        sampraMembership: formData.sampra_membership,
        impraMembership: formData.impra_membership,
        collaborators: formData.collaborators.map(c => ({
          name: c.full_legal_name,
          stage_name: c.stage_name,
          email: c.email,
          phone: c.phone,
          role: c.role,
          contribution_type: c.contribution_type,
          contribution_description: c.contribution_description,
          percentage: parseFloat(String(c.ownership_percentage)),
          id_number: c.id_number,
          passport_number: c.passport_number,
          nationality: c.nationality,
          address_line1: c.address_line1,
          address_line2: c.address_line2,
          city: c.city,
          province: c.province,
          postal_code: c.postal_code,
          country: c.country,
          date_of_birth: c.date_of_birth,
          publisher_name: c.publisher_name,
          publisher_contact: c.publisher_contact,
          samro_membership: c.samro_membership,
          samro_account_number: c.samro_account_number,
          capasso_membership: c.capasso_membership,
          capasso_account_number: c.capasso_account_number,
          sampra_membership: c.sampra_membership,
          sampra_account_number: c.sampra_account_number,
          impra_membership: c.impra_membership,
          impra_account_number: c.impra_account_number,
          writer_share: c.writer_share,
          producer_share: c.producer_share,
          performer_share: c.performer_share,
          is_minor: c.is_minor,
          guardian_name: c.guardian_name,
          guardian_id_number: c.guardian_id_number,
          guardian_relationship: c.guardian_relationship,
          guardian_contact: c.guardian_contact
        }))
      });

      navigate(`/split-sheets/${response.data.splitSheet.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create split sheet');
    } finally {
      setLoading(false);
    }
  };

  const totalPercentage = calculateTotalPercentage();
  const isValid = Math.abs(totalPercentage - 100) < 0.01;

  const steps = [
    { number: 1, title: 'Song Information', description: 'Basic song details and metadata' },
    { number: 2, title: 'Recording Details', description: 'Studio and production information' },
    { number: 3, title: 'Publishing & PRO', description: 'Publishing and rights organization info' },
    { number: 4, title: 'Contributors', description: 'All songwriters and contributors' },
    { number: 5, title: 'Review & Create', description: 'Final review and creation' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Create South African Split Sheet</h1>
        <p className="mt-1 text-sm text-gray-600">
          Create a legally enforceable split sheet agreement in accordance with South African music industry standards
        </p>
      </div>
      <div className="mb-8">
        <HelpGuide
          title="How to complete this SA form"
          intro="Complete one step at a time using Next and Previous."
          steps={[
            { title: 'Step 1-3', detail: 'Capture song, recording, and publishing information.' },
            { title: 'Step 4', detail: 'Add contributors with legal details and ownership percentages.' },
            { title: 'Step 5', detail: 'Review carefully, then create the split sheet.' },
          ]}
        />
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep >= step.number 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {step.number}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  currentStep >= step.number ? 'text-primary-600' : 'text-gray-500'
                }`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-500">{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${
                  currentStep > step.number ? 'bg-primary-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Step 1: Song Information */}
        {currentStep === 1 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">🎶 Song Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Song Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.song_title}
                  onChange={(e) => updateFormData('song_title', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter final song title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Date of Creation *
                </label>
                <input
                  type="date"
                  required
                  value={formData.date_of_creation}
                  onChange={(e) => updateFormData('date_of_creation', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Alternate Titles / Working Titles
                </label>
                <input
                  type="text"
                  value={formData.alternate_titles}
                  onChange={(e) => updateFormData('alternate_titles', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Previous names, working titles"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Session Date
                </label>
                <input
                  type="date"
                  value={formData.session_date}
                  onChange={(e) => updateFormData('session_date', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  ISRC Code
                </label>
                <input
                  type="text"
                  value={formData.isrc_code}
                  onChange={(e) => updateFormData('isrc_code', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="International Standard Recording Code"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  ISWC Code
                </label>
                <input
                  type="text"
                  value={formData.iswc_code}
                  onChange={(e) => updateFormData('iswc_code', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="International Standard Musical Work Code"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Album / Project Name
                </label>
                <input
                  type="text"
                  value={formData.album_name}
                  onChange={(e) => updateFormData('album_name', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Album or project name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Genre
                </label>
                <select
                  value={formData.genre}
                  onChange={(e) => updateFormData('genre', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="">Select Genre</option>
                  <option value="Pop">Pop</option>
                  <option value="Hip Hop">Hip Hop</option>
                  <option value="R&B">R&B</option>
                  <option value="Rock">Rock</option>
                  <option value="Jazz">Jazz</option>
                  <option value="Classical">Classical</option>
                  <option value="Electronic">Electronic</option>
                  <option value="Afro">Afro</option>
                  <option value="Gospel">Gospel</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                Additional Song Details
              </label>
              <textarea
                rows={3}
                value={formData.song_details}
                onChange={(e) => updateFormData('song_details', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Additional song information, lyrics themes, etc."
              />
            </div>
          </div>
        )}

        {/* Step 2: Recording Details */}
        {currentStep === 2 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">🧾 Recording & Production Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Studio Name
                </label>
                <input
                  type="text"
                  value={formData.studio_name}
                  onChange={(e) => updateFormData('studio_name', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Recording studio name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Studio Location
                </label>
                <input
                  type="text"
                  value={formData.studio_location}
                  onChange={(e) => updateFormData('studio_location', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="City, Province, Country"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Recording Engineer
                </label>
                <input
                  type="text"
                  value={formData.recording_engineer}
                  onChange={(e) => updateFormData('recording_engineer', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Recording engineer name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Mixer
                </label>
                <input
                  type="text"
                  value={formData.mixer}
                  onChange={(e) => updateFormData('mixer', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Mix engineer name"
                />
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Instruments Used
                </label>
                <textarea
                  rows={2}
                  value={formData.instruments_used}
                  onChange={(e) => updateFormData('instruments_used', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="List all instruments used in the recording"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Samples Used
                </label>
                <textarea
                  rows={2}
                  value={formData.samples_used}
                  onChange={(e) => updateFormData('samples_used', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="List any samples or loops used"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Sample Clearances
                </label>
                <textarea
                  rows={2}
                  value={formData.sample_clearances}
                  onChange={(e) => updateFormData('sample_clearances', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Details of any sample clearances obtained"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Publishing & PRO */}
        {currentStep === 3 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">💸 Publishing & Rights Organizations</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Publisher Name
                </label>
                <input
                  type="text"
                  value={formData.publisher_name}
                  onChange={(e) => updateFormData('publisher_name', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Publishing company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Admin Name
                </label>
                <input
                  type="text"
                  value={formData.admin_name}
                  onChange={(e) => updateFormData('admin_name', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Administration company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Publisher Contact
                </label>
                <input
                  type="text"
                  value={formData.publisher_contact}
                  onChange={(e) => updateFormData('publisher_contact', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Publisher contact information"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Copyright Owner
                </label>
                <input
                  type="text"
                  value={formData.copyright_owner}
                  onChange={(e) => updateFormData('copyright_owner', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Primary copyright holder"
                />
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-md font-medium text-gray-900 mb-4">🇿🇦 South African PRO Memberships</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SAMRO Membership
                  </label>
                  <input
                    type="text"
                    value={formData.samro_membership}
                    onChange={(e) => updateFormData('samro_membership', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="SAMRO membership details"
                  />
                  <p className="mt-1 text-xs text-gray-500">Performance rights organization</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    CAPASSO Membership
                  </label>
                  <input
                    type="text"
                    value={formData.capasso_membership}
                    onChange={(e) => updateFormData('capasso_membership', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="CAPASSO membership details"
                  />
                  <p className="mt-1 text-xs text-gray-500">Mechanical rights organization</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SAMPRA Membership
                  </label>
                  <input
                    type="text"
                    value={formData.sampra_membership}
                    onChange={(e) => updateFormData('sampra_membership', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="SAMPRA membership details"
                  />
                  <p className="mt-1 text-xs text-gray-500">Needletime rights organization</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    IMPRA Membership
                  </label>
                  <input
                    type="text"
                    value={formData.impra_membership}
                    onChange={(e) => updateFormData('impra_membership', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="IMPRA membership details"
                  />
                  <p className="mt-1 text-xs text-gray-500">Independent music rights organization</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Contributors */}
        {currentStep === 4 && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">👥 Contributors & Ownership</h2>
              <button
                type="button"
                onClick={addCollaborator}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="-ml-0.5 mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Contributor
              </button>
            </div>

            <div className="space-y-6">
              {formData.collaborators.map((collaborator, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-sm font-medium text-gray-700">
                      Contributor {index + 1}
                    </h3>
                    {formData.collaborators.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCollaborator(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Basic Information */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Full Legal Name *</label>
                      <input
                        type="text"
                        required
                        value={collaborator.full_legal_name}
                        onChange={(e) => updateCollaborator(index, 'full_legal_name', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Full legal name for contracts"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Stage Name</label>
                      <input
                        type="text"
                        value={collaborator.stage_name}
                        onChange={(e) => updateCollaborator(index, 'stage_name', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Artist/stage name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email *</label>
                      <input
                        type="email"
                        required
                        value={collaborator.email}
                        onChange={(e) => updateCollaborator(index, 'email', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <input
                        type="tel"
                        value={collaborator.phone}
                        onChange={(e) => updateCollaborator(index, 'phone', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="+27 XX XXX XXXX"
                      />
                    </div>

                    {/* Role and Contribution */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Role</label>
                      <select
                        value={collaborator.role}
                        onChange={(e) => updateCollaborator(index, 'role', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      >
                        <option value="Artist">Artist</option>
                        <option value="Songwriter">Songwriter</option>
                        <option value="Producer">Producer</option>
                        <option value="Composer">Composer</option>
                        <option value="Lyricist">Lyricist</option>
                        <option value="Featured Artist">Featured Artist</option>
                        <option value="Engineer">Engineer</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contribution Type</label>
                      <select
                        value={collaborator.contribution_type}
                        onChange={(e) => updateCollaborator(index, 'contribution_type', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      >
                        <option value="Composer">Composer</option>
                        <option value="Lyricist">Lyricist</option>
                        <option value="Producer">Producer</option>
                        <option value="Performer">Performer</option>
                        <option value="Arranger">Arranger</option>
                        <option value="Engineer">Engineer</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ownership % *</label>
                      <input
                        type="number"
                        required
                        min="0"
                        max="100"
                        step="0.01"
                        value={collaborator.ownership_percentage}
                        onChange={(e) => updateCollaborator(index, 'ownership_percentage', parseFloat(e.target.value) || 0)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="0.00"
                      />
                    </div>

                    {/* SA Legal Information */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">SA ID Number *</label>
                      <input
                        type="text"
                        value={collaborator.id_number}
                        onChange={(e) => updateCollaborator(index, 'id_number', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="13-digit ID number"
                        pattern="[0-9]{13}"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Passport Number</label>
                      <input
                        type="text"
                        value={collaborator.passport_number}
                        onChange={(e) => updateCollaborator(index, 'passport_number', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="For non-SA citizens"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Nationality</label>
                      <input
                        type="text"
                        value={collaborator.nationality}
                        onChange={(e) => updateCollaborator(index, 'nationality', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="South African"
                      />
                    </div>

                    {/* Address Information */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
                      <input
                        type="text"
                        value={collaborator.address_line1}
                        onChange={(e) => updateCollaborator(index, 'address_line1', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Street address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <input
                        type="text"
                        value={collaborator.city}
                        onChange={(e) => updateCollaborator(index, 'city', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="City"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Province</label>
                      <select
                        value={collaborator.province}
                        onChange={(e) => updateCollaborator(index, 'province', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      >
                        <option value="">Select Province</option>
                        <option value="Eastern Cape">Eastern Cape</option>
                        <option value="Free State">Free State</option>
                        <option value="Gauteng">Gauteng</option>
                        <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                        <option value="Limpopo">Limpopo</option>
                        <option value="Mpumalanga">Mpumalanga</option>
                        <option value="Northern Cape">Northern Cape</option>
                        <option value="North West">North West</option>
                        <option value="Western Cape">Western Cape</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                      <input
                        type="text"
                        value={collaborator.postal_code}
                        onChange={(e) => updateCollaborator(index, 'postal_code', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="XXXX"
                      />
                    </div>

                    {/* PRO Memberships */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">SAMRO Membership</label>
                      <input
                        type="text"
                        value={collaborator.samro_membership}
                        onChange={(e) => updateCollaborator(index, 'samro_membership', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="SAMRO membership"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">SAMRO Account #</label>
                      <input
                        type="text"
                        value={collaborator.samro_account_number}
                        onChange={(e) => updateCollaborator(index, 'samro_account_number', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="SAMRO account number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">CAPASSO Membership</label>
                      <input
                        type="text"
                        value={collaborator.capasso_membership}
                        onChange={(e) => updateCollaborator(index, 'capasso_membership', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="CAPASSO membership"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">CAPASSO Account #</label>
                      <input
                        type="text"
                        value={collaborator.capasso_account_number}
                        onChange={(e) => updateCollaborator(index, 'capasso_account_number', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="CAPASSO account number"
                      />
                    </div>

                    {/* Royalty Breakdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Writer Share %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={collaborator.writer_share}
                        onChange={(e) => updateCollaborator(index, 'writer_share', parseFloat(e.target.value) || 0)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Producer Share %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={collaborator.producer_share}
                        onChange={(e) => updateCollaborator(index, 'producer_share', parseFloat(e.target.value) || 0)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Performer Share %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={collaborator.performer_share}
                        onChange={(e) => updateCollaborator(index, 'performer_share', parseFloat(e.target.value) || 0)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="0.00"
                      />
                    </div>

                    {/* Minor Protection */}
                    <div className="md:col-span-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={collaborator.is_minor}
                          onChange={(e) => updateCollaborator(index, 'is_minor', e.target.checked)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                          This contributor is a minor (under 18 years old)
                        </label>
                      </div>
                    </div>

                    {/* Guardian Information (if minor) */}
                    {collaborator.is_minor && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Guardian Name</label>
                          <input
                            type="text"
                            value={collaborator.guardian_name}
                            onChange={(e) => updateCollaborator(index, 'guardian_name', e.target.value)}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            placeholder="Guardian full name"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Guardian ID Number</label>
                          <input
                            type="text"
                            value={collaborator.guardian_id_number}
                            onChange={(e) => updateCollaborator(index, 'guardian_id_number', e.target.value)}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            placeholder="13-digit ID number"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Relationship</label>
                          <select
                            value={collaborator.guardian_relationship}
                            onChange={(e) => updateCollaborator(index, 'guardian_relationship', e.target.value)}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          >
                            <option value="">Select Relationship</option>
                            <option value="Parent">Parent</option>
                            <option value="Legal Guardian">Legal Guardian</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </>
                    )}

                    {/* Contribution Description */}
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-gray-700">Contribution Description</label>
                      <textarea
                        rows={2}
                        value={collaborator.contribution_description}
                        onChange={(e) => updateCollaborator(index, 'contribution_description', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Describe the specific contribution to the musical work"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Total Percentage Display */}
            <div className={`mt-6 p-4 rounded-md ${isValid ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <div className="flex justify-between items-center">
                <span className={`text-sm font-medium ${isValid ? 'text-green-800' : 'text-yellow-800'}`}>
                  Total Ownership Percentage:
                </span>
                <span className={`text-lg font-bold ${isValid ? 'text-green-900' : 'text-yellow-900'}`}>
                  {totalPercentage.toFixed(2)}%
                </span>
              </div>
              {!isValid && (
                <p className="mt-1 text-xs text-yellow-700">
                  Must equal 100%. Adjust percentages to continue.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Review */}
        {currentStep === 5 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">⚖️ Review & Legal Compliance</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Song Information</h3>
                <p className="text-sm text-gray-600">{formData.song_title}</p>
                {formData.song_details && <p className="text-sm text-gray-600">{formData.song_details}</p>}
                <p className="text-sm text-gray-600">Created: {new Date(formData.date_of_creation).toLocaleDateString('en-ZA')}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700">Contributors ({formData.collaborators.length})</h3>
                <div className="mt-2 space-y-1">
                  {formData.collaborators.map((collab, index) => (
                    <div key={index} className="text-sm text-gray-600">
                      {collab.full_legal_name} ({collab.stage_name}) - {collab.role} ({collab.ownership_percentage}%)
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700">Total Ownership</h3>
                <p className="text-sm text-gray-600">{totalPercentage.toFixed(2)}%</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700">Legal Compliance</h3>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-600">South African Law Compliant</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-600">SAMRO Registration Ready</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-600">Legally Enforceable</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Legal Notice</h3>
              <p className="text-xs text-blue-700">
                This split sheet will be generated with all necessary South African legal clauses and will be 
                legally binding under South African law. All parties will be required to provide digital signatures
                in accordance with the Electronic Communications and Transactions Act (ECTA).
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>

            {currentStep < 5 ? (
              <button
                type="button"
                onClick={() => setCurrentStep(Math.min(5, currentStep + 1))}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading || !isValid}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Split Sheet'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateSplitSheetSA;
